/**
 * @deprecated Header is deprecated pending LD rollout. HeaderV2 will replace this component. (pos.iam.org-management-2024-07.rollout)
 * @see HeaderV2
 */
import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { down, up } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled, { css } from 'styled-components';

import UserActions from 'src/app_deprecated/actions/UserActions';
import CompanyApi from 'src/app_deprecated/api/CompanyApi';
import UserApi from 'src/app_deprecated/api/UserApi';
import { endPoints as Endpoints } from 'src/app_deprecated/constants/AppConstants';
import { Permissions } from 'src/app_deprecated/constants/PermissionsConstants';
import { actions as ActionTypes } from 'src/app_deprecated/constants/UserConstants';
import UserStore from 'src/app_deprecated/stores/UserStore';
import AppDispatcher from 'src/app_deprecated/utils/AppDispatcher';
import HttpClient from 'src/app_deprecated/utils/HttpClient';

import { AccountIcon } from 'assets/icons/account-icon';
import { HelpIcon } from 'assets/icons/help-icon';
import { DutchieLogo } from 'src/app/components/icons/dutchie-logo';
import { SidebarMenu } from 'src/app/components/icons/sidebar-menu';
import { Menu } from 'src/app/components/lib/menu';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { Select } from 'src/app/components/lib/select';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import { ElevateSuperuserModal } from 'src/app/layout/header/elevate-superuser-modal';
import { useDarkMode } from 'src/app/state/dark-mode';
import { useNotificationActions } from 'src/app/state/notifications';
import { userAtom } from 'src/app/state/user';
import { userDispensariesAtom } from 'src/app/state/user-dispensaries';
import { usePermissionCheck, userPermissionsAtom } from 'src/app/state/user-permissions';

import NavigationMenu from '../navigation-menu';
import NestedNavigationMenu from '../navigation-menu/nested-menu';
import { ServerMigrationBanner } from '../server-migration-ui/server-migration-banner';

import { GeneralHeaderBanner } from './banners/general-header-banner';
import { LocationMessagingBanner } from './banners/location-messaging-banner';
import { LocationSuspensionBanner } from './banners/location-suspension-banner';
import { NonSandboxBanner } from './banners/non-sandbox-banner';
import { HEADER_HEIGHT } from './banners/shared';
import { useDecagon } from './decagon/use-decagon';
import GlobalSearch from './global-search';
import { LDExplorerModal } from './ld-explorer-modal';
import { SuperUserLocationSelect } from './superuser-location-select';
import { VersionsModal } from './versions-modal';

export function Header() {
  const darkMode = useDarkMode();
  const [viewVersionsModal, toggleVersionsModal] = useState(false);
  const [viewLDExplorer, toggleLDExplorer] = useState(false);
  const [viewElevateSuperUser, toggleElevateSuperUser] = useState(false);
  const { perms } = useRecoilValue(userPermissionsAtom);
  const [accountMenu, setAccountMenu] = useState<HTMLElement | null>(null);
  const [helpMenu, setHelpMenu] = useState<HTMLElement | null>(null);
  const [helpDeskUrl, setHelpDeskUrl] = useState('');
  const [openSidebar, setOpenSidebar] = useState(false);
  const { decagon } = useDecagon();
  // react query client -- used for clearing RQ cache upon lsp/loc change and logout
  const queryClient = useQueryClient();
  const notificationActions = useNotificationActions();
  const isSuperUser = usePermissionCheck(Permissions.SuperUser);

  const isAccountMenuOpen = Boolean(accountMenu);
  const isHelpMenuOpen = Boolean(helpMenu);

  const flags = useFlags();
  const showLocationPublish = perms[Permissions.SuperUser];
  const hideGlobalSearch = flags['pos.bo.disable-global-search'];
  const showAutocompleteLocationPicker = flags['pos.backoffice.enable-autocomplete-location-picker.rollout'];
  const showElevateSuperUser = flags['pos.platform.pos-readonly-superuser.rollout'];

  const serverMigrationConfig = flags['pos.platform.database-migration-configs.operational'];
  const nonSandboxBannerRollout = useLDFlag<boolean>(LD.NON_SANDBOX_BANNER_ALERT, false);

  const refreshPageOnLspOrLocChange =
    useLDFlag<boolean>(LD.REFRESH_PAGE_ON_LSP_OR_LOC_CHANGE, false) &&
    window.location.pathname.includes('/superuser/edit-lsp');

  const { locations, selectedLocation, companies, selectedCompany } = useRecoilValue(userDispensariesAtom);
  const { Id, FullName } = useRecoilValue(userAtom);

  const navigate = useNavigate();

  const desktopView = useBreakpoint(up('largeTablet'));

  async function changeCompany({ target }) {
    const company = companies.find(({ LspId }) => LspId === parseInt(target.value, 10));
    const formData = UserStore.getApiData();

    // first get the locations associated with the selected company
    const newLocations = await CompanyApi.getLocations({
      ...formData,
      LspId: company.LspId,
    });
    const locationWithAccess = (newLocations.Data || []).find((loc) => loc.HasPermission);

    // fwiw I _believe_ this check should be done on the server. We shouldn't be sending down lsp's that
    // the user has no locs available under
    if (!locationWithAccess) {
      // notify the user that they can't access this company(lsp)
      notificationActions.alert({
        message: 'Unauthorized',
        subContent: 'It appears you do not have access to view the requested company',
      });
      return;
    }

    // set the location's lsp id to the id  of the lsp selected from the menu
    locationWithAccess.LspId = company.LspId;

    // clear the RQ cache
    queryClient.clear();

    // now grab images and set default lsp
    void UserApi.setDefaultLsp(formData.SessionId, UserStore.getState().data.Id, company.LspId);
    // this could possibly be removed. We show logo on invoices and in settings.
    const images = await CompanyApi.getLogo({ ...formData, LspId: company.LspId });

    // then set the location and notify all listening stores
    UserActions.selectLocation(locationWithAccess);
    AppDispatcher.dispatch({
      actionType: ActionTypes.SELECT_COMPANY,
      data: {
        locations: newLocations.Data || [],
        currentCompany: { ...company, Logo: images.Data[0].Logo },
        currentLocation: locationWithAccess,
      },
    });

    if (refreshPageOnLspOrLocChange) {
      window.location.reload();
    }
  }

  function changeLocation({ target }) {
    const location = locations.find(({ LocId }) => LocId === parseInt(target.value, 10));
    queryClient.clear();
    UserActions.selectLocation(location);

    if (refreshPageOnLspOrLocChange) {
      window.location.reload();
    }
  }

  function handleLDExplorerClick() {
    toggleLDExplorer(!viewLDExplorer);
    setHelpMenu(null);
  }

  useEffect(() => {
    void HttpClient.post(Endpoints.GET_HELP_DESK, UserStore.getApiData()).then((res) => {
      setHelpDeskUrl(res.HelpDeskUrl);
    });
  }, [selectedCompany]);

  useEffect(() => {
    setAccountMenu(null);
  }, [viewVersionsModal]);

  function handleNavigationClick(navigateTo: string) {
    if (navigateTo === 'account') {
      navigate('settings/account');
      setAccountMenu(null);
    }

    if (navigateTo === 'logout') {
      queryClient.clear();
      UserStore.logout();
      setAccountMenu(null);
    }
  }

  return (
    <>
      {viewVersionsModal && <VersionsModal onClose={() => toggleVersionsModal(false)} />}
      {viewLDExplorer && <LDExplorerModal onClose={handleLDExplorerClick} />}
      {viewElevateSuperUser && <ElevateSuperuserModal onClose={() => toggleElevateSuperUser(false)} />}
      {!!serverMigrationConfig?.backofficeURL && <ServerMigrationBanner migrationConfig={serverMigrationConfig} />}
      <GeneralHeaderBanner />
      {nonSandboxBannerRollout && <NonSandboxBanner />}
      <LocationMessagingBanner />
      <LocationSuspensionBanner />
      <HeaderContainer $darkMode={darkMode}>
        {desktopView ? (
          <NavigationMenu isOpen={openSidebar} onClose={() => setOpenSidebar(false)} />
        ) : (
          <NestedNavigationMenu isOpen={openSidebar} onClose={() => setOpenSidebar(false)} />
        )}

        <HeaderLeftContentContainer>
          <SidebarToggleLogoContainer>
            <ToggleContainer onClick={() => setOpenSidebar(true)}>
              <SidebarMenu />
            </ToggleContainer>
            {desktopView && <DutchieLogo onClick={() => navigate('home')} />}
          </SidebarToggleLogoContainer>
          {!hideGlobalSearch && <GlobalSearch />}
        </HeaderLeftContentContainer>

        <HeaderRightContentContainer>
          {desktopView && (!isSuperUser || !showAutocompleteLocationPicker) && (
            <>
              <SelectHeader
                automationId='header_select_company'
                hideBorder
                value={String(selectedCompany?.LspId ?? '')}
                onChange={(e) => changeCompany(e)}
              >
                {(companies || []).map((company) => (
                  <MenuItem
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    data-testid={`rebrand-header_menu-item_${company.LspName}`}
                    key={company.LspId}
                    sx={{ fontSize: '12px' }}
                    value={company.LspId}
                  >
                    {company.LspName}
                  </MenuItem>
                ))}
              </SelectHeader>

              <SelectHeader
                automationId='header_select_location'
                hideBorder
                value={selectedLocation.LocId}
                onChange={(e) => changeLocation(e)}
              >
                {(locations || [])
                  .filter((location) => location.HasPermission || location.LocId === selectedLocation.LocId)
                  .map((location) => (
                    <MenuItem
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                      data-testid={`rebrand-header_menu-item_${location.Name}`}
                      key={location.LocId}
                      sx={{ fontSize: '12px' }}
                      value={location.LocId}
                    >
                      {!location.IsLive && showLocationPublish ? `${location.Name} - STAGING` : location.Name}
                    </MenuItem>
                  ))}
              </SelectHeader>
            </>
          )}
          {desktopView && isSuperUser && showAutocompleteLocationPicker && (
            <SuperUserLocationSelect changeCompany={changeCompany} changeLocation={changeLocation} />
          )}

          <IconContainer>
            <AccountIcon onClick={(e) => setAccountMenu(e.currentTarget)} />
          </IconContainer>

          <IconContainer>
            <HelpIcon onClick={(e) => setHelpMenu(e.currentTarget)} />
          </IconContainer>
        </HeaderRightContentContainer>
      </HeaderContainer>

      {/* Account Menu */}
      <StyledMenu anchorEl={accountMenu} open={isAccountMenuOpen} onClose={() => setAccountMenu(null)}>
        <AccountVersionsMenuItem $darkMode={darkMode} value=''>
          <AccountFullName data-testid='rebrand-header_div_user-full-name'>{FullName}</AccountFullName>
          <AccountVersions>
            <div title='User ID'>{Id}</div>
            <div>
              <AccountVersionsButton type='button' onClick={() => toggleVersionsModal(true)}>
                Versions
              </AccountVersionsButton>
            </div>
          </AccountVersions>
        </AccountVersionsMenuItem>
        <AccountMenuItem
          $darkMode={darkMode}
          data-testid='rebrand-header_account-menu-item_my-account'
          value='My account'
          onClick={() => handleNavigationClick('account')}
        >
          My account
        </AccountMenuItem>
        {isSuperUser && showElevateSuperUser && (
          <AccountMenuItem
            $darkMode={darkMode}
            data-testid='rebrand-header_account-menu-item_enable_edit_access'
            value='Settings'
            onClick={() => {
              setAccountMenu(null);
              setHelpMenu(null);
              toggleElevateSuperUser(true);
            }}
          >
            Enable edit access
          </AccountMenuItem>
        )}
        <AccountMenuItem
          $darkMode={darkMode}
          data-testid='rebrand-header_account-menu-item_logout'
          value='Logout'
          onClick={() => handleNavigationClick('logout')}
        >
          Logout
        </AccountMenuItem>
      </StyledMenu>

      {/* Help Menu */}
      <StyledMenu anchorEl={helpMenu} open={isHelpMenuOpen} onClose={() => setHelpMenu(null)}>
        {decagon && (
          <HelpMenuItem
            $darkMode={darkMode}
            value='Live chat'
            onClick={() => {
              decagon.open();
              setHelpMenu(null);
            }}
          >
            <span>Live chat</span>
          </HelpMenuItem>
        )}
        <a href='https://support.dutchie.com/hc/en-us' rel='noopener noreferrer' target='_blank'>
          <HelpMenuItem $darkMode={darkMode} value='Help center'>
            Help center
          </HelpMenuItem>
        </a>
        {helpDeskUrl && (
          <a href={`//${helpDeskUrl}`} rel='noopener noreferrer' target='_blank'>
            <HelpMenuItem $darkMode={darkMode} value='Internal help desk'>
              Internal help desk
            </HelpMenuItem>
          </a>
        )}
        {isSuperUser && (
          <HelpMenuItem $darkMode={darkMode} value='LD Explorer' onClick={handleLDExplorerClick}>
            LD Explorer
          </HelpMenuItem>
        )}
      </StyledMenu>
    </>
  );
}

const HeaderContainer = styled.div<{ $darkMode: boolean }>`
  display: flex;
  height: ${HEADER_HEIGHT}px;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  left: 0;
  background-color: var(--color-opal-90);
  z-index: 10;
  padding: 0 var(--sizes-50) 0 var(--sizes-40);

  @media only screen and (max-width: 768px) {
    svg:nth-child(2) {
      display: none;
    }
  }

  .select-form-control {
    display: block;
    width: fit-content;
    margin-bottom: 0;

    fieldset {
      border: none;
      padding: 0;
    }

    .MuiOutlinedInput-root {
      padding: 0;
    }

    .MuiSelect-select {
      font: var(--font-small-13pt-normal);
    }
  }

  .sub-navigation-panel {
    &-enter {
      opacity: 0;
      transform: translateX(-100%);
    }
    &-enter-done {
      opacity: 1;
      transform: translateX(0%);
      transition: opacity 300ms linear, transform 300ms ease-in;
    }
    &-exit {
      opacity: 1;
      transform: translateX(0%);
    }
    &-exit-active {
      opacity: 0;
      transform: translateX(-100%);
      transition: opacity 300ms linear, transform 300ms ease-in;
    }
  }

  .global_search_icon-focused {
    display: none;
  }

  ${({ $darkMode }) =>
    $darkMode &&
    css`
      border-bottom: 1px solid #1f3244;
    `}
`;

const HeaderLeftContentContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
`;

const SidebarToggleLogoContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  height: 100%;

  ${down('largeTablet')} {
    padding: 0;
  }

  svg:hover {
    cursor: pointer;
  }
`;

const ToggleContainer = styled.div`
  padding: var(--sizes-20);
  border-radius: 50%;
  display: flex;
  margin-right: var(--sizes-40);

  :hover {
    cursor: pointer;
    background-color: rgba(255, 255, 255, 0.16);
  }
`;

const SelectHeader = styled(Select)`
  background: none;
  width: 100%;
`;

const HeaderRightContentContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sizes-60);

  .select-select-input {
    font-family: var(--font-family-text);
    background: none;

    .MuiInputBase-input {
      padding-left: 0;
      padding-right: var(--sizes-60);
    }

    > svg {
      width: var(--sizes-30);
      height: 7px;
      top: calc(50% - 0.2em);
      right: -10px;
      margin-right: 10px;

      path {
        fill: var(--color-gray-10);
      }
    }

    &.Mui-focused .MuiSelect-icon.MuiSelect-iconOutlined {
      path {
        fill: var(--color-gray-10);
      }
    }
  }

  .MuiInputBase-input {
    color: var(--color-gray-10);
    line-height: var(--sizes-40);
  }
`;

const IconContainer = styled.div`
  display: flex;

  svg {
    display: block;
    path {
      fill: var(--color-gray-10);
    }
  }

  :hover {
    cursor: pointer;
    background: none;
  }
`;

const StyledMenu = styled(Menu)`
  margin-top: var(--sizes-20);

  .MuiPopover-paper {
    width: fit-content;
    max-width: 400px;
    min-width: 200px;
  }
`;

const StyledMenuItem = styled(MenuItem)<{ $darkMode: boolean }>`
  height: 52px;
  color: #000000;
  padding: var(--sizes-40) var(--sizes-70);
  background: none;

  .Mui-focusVisible {
    background-color: none;
    background: none;
  }

  :hover {
    background: transparent;
    background-color: #f3f3f3;
    font-weight: 600;
  }
  ${({ $darkMode }) =>
    $darkMode &&
    css`
      color: var(--color-brand-primary-white);
      :hover {
        background: transparent;
        background-color: var(--color-gray-90);
      }
    `}
`;

const AccountMenuItem = styled(StyledMenuItem)`
  :nth-child(1) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: none;
    align-items: flex-start;
    padding: var(--sizes-20) var(--sizes-70) var(--sizes-30) var(--sizes-70);

    .logged-in-as {
      font: var(--font-micro-10pt-bold);
      margin-bottom: 2px;
      color: #a6a6a6;
    }

    :hover {
      cursor: auto;
      font-weight: 400;
      background: transparent;
      border-radius: 9px 9px 0 0;
    }

    :disabled {
      color: #000000;
    }

    .Mui-focusVisible {
      background-color: none;
    }
  }

  :nth-child(3) {
    border-bottom: none;
  }
`;

const AccountVersionsMenuItem = styled(StyledMenuItem)`
  height: auto;
  flex-direction: column;
  align-items: flex-start;
  border-bottom: 1px solid var(--color-gray-40);
  justify-content: space-between;
  padding-top: var(--sizes-30);
  padding-bottom: var(--sizes-30);
  margin-bottom: var(--sizes-20);

  :hover,
  :focus {
    background: transparent;
    cursor: auto;
  }
`;

const AccountFullName = styled.div`
  font: var(--font-extra-small-12pt-semibold);
  text-wrap: wrap;
`;

const AccountVersions = styled.div`
  color: var(--color-gray-70);
  font-size: var(--typography-mini-11pt-semibold-font-size);
  font-weight: var(--typography-extra-small-12pt-normal-font-weight);
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const AccountVersionsButton = styled.button`
  background: none;
  color: var(--color-brand-secondary-sapphire);
  padding: 0;

  :hover {
    text-decoration: underline;
  }
`;

const HelpMenuItem = styled(StyledMenuItem)`
  a {
    color: #000000;
  }
`;
